/* All Call Recordings page — full library with attribution + filters.
 *
 * Solves two real problems:
 *  1. Recordings that didn't match a lead/buyer by phone (orphans) had no
 *     way to be found from the UI. They now show up here with a "Match"
 *     button that re-runs attribution + lets the user manually re-link.
 *  2. Quickly scan all recordings across leads + buyers in one place, with
 *     coach scores and AI summaries visible inline.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mic, Loader2, Link2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import CallRecordingsList from "@/components/CallRecordingsList";

export default function Recordings() {
  const [tab, setTab] = useState("all"); // all | orphans
  const [counts, setCounts] = useState({ all: 0, orphans: 0 });
  const [refresh, setRefresh] = useState(0);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [reattributing, setReattributing] = useState(false);
  const [orphans, setOrphans] = useState([]);

  const loadCounts = useCallback(async () => {
    try {
      const [allR, orphR] = await Promise.all([
        api.get("/voice/recordings", { params: { limit: 200 } }),
        api.get("/voice/recordings", { params: { orphans: true, limit: 200 } }),
      ]);
      setCounts({ all: allR.data.total, orphans: orphR.data.total });
      setOrphans(orphR.data.items || []);
    } finally {
      setLoadingCounts(false);
    }
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts, refresh]);

  const reattributeAll = async () => {
    if (!orphans.length) return;
    if (!window.confirm(
      `Try to re-match ${orphans.length} orphan recordings to leads / buyers? ` +
      `This will fetch the actual dialed number from Twilio for each.`)) return;
    setReattributing(true);
    let matched = 0;
    for (const rec of orphans) {
      try {
        const { data } = await api.post(`/voice/recordings/${rec.id}/reattribute`);
        if (data.matched_lead || data.matched_buyer) matched += 1;
      } catch (e) {
        console.error("reattribute failed", rec.id, e);
      }
    }
    setReattributing(false);
    toast.success(`Re-matched ${matched} of ${orphans.length} recordings`);
    setRefresh((r) => r + 1);
  };

  return (
    <div className="pb-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
            <Mic size={18} className="text-indigo-600" />
          </div>
          <div>
            <div className="label-eyebrow">Outreach</div>
            <h1 className="font-display font-bold text-2xl">Call Recordings</h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => setTab("all")}
          className={
            "px-3 py-1.5 rounded-md text-sm font-bold transition " +
            (tab === "all"
              ? "bg-[var(--ink)] text-white"
              : "bg-white border border-[var(--line)] text-[var(--ink-2)] hover:border-[var(--ink)]")
          }
          data-testid="recordings-tab-all"
        >
          All <span className="opacity-60 ml-1">{loadingCounts ? "…" : counts.all}</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("orphans")}
          className={
            "px-3 py-1.5 rounded-md text-sm font-bold transition flex items-center gap-1.5 " +
            (tab === "orphans"
              ? "bg-amber-500 text-white"
              : "bg-white border border-[var(--line)] text-[var(--ink-2)] hover:border-amber-500")
          }
          data-testid="recordings-tab-orphans"
        >
          {counts.orphans > 0 && <AlertTriangle size={12} />}
          Unmatched <span className="opacity-60 ml-1">{loadingCounts ? "…" : counts.orphans}</span>
        </button>
        <div className="flex-1" />
        {tab === "orphans" && counts.orphans > 0 && (
          <button
            type="button"
            onClick={reattributeAll}
            disabled={reattributing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition disabled:bg-emerald-300"
            data-testid="recordings-reattribute-all"
          >
            {reattributing
              ? <><Loader2 size={12} className="animate-spin" /> Matching…</>
              : <><Link2 size={12} /> Try to match all</>}
          </button>
        )}
        <button
          type="button"
          onClick={() => setRefresh((r) => r + 1)}
          className="w-9 h-9 rounded-md border border-[var(--line)] hover:border-[var(--ink)] flex items-center justify-center transition"
          title="Refresh"
          data-testid="recordings-refresh"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Helper for orphans tab */}
      {tab === "orphans" && counts.orphans > 0 && (
        <div className="card mb-4 bg-amber-50/50 border-amber-200" data-testid="recordings-orphan-hint">
          <div className="flex items-start gap-2 text-xs">
            <AlertTriangle size={13} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="text-amber-900 leading-relaxed">
              These recordings weren't auto-matched to a lead or buyer. Usually because:
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>The number called isn't in your CRM yet (e.g. test calls to yourself)</li>
                <li>The number was stored with a different format</li>
                <li>An early bug stored the TwiML proxy address instead of the real dialed number</li>
              </ul>
              Click <strong>"Try to match all"</strong> above to fetch the real numbers from Twilio
              and re-run attribution against your CRM.
            </div>
          </div>
        </div>
      )}

      {/* Use the same list component as Lead/Buyer detail — feed it without filters */}
      <AllRecordingsList key={`${tab}-${refresh}`} tab={tab} />
    </div>
  );
}

// Thin wrapper: CallRecordingsList accepts leadId/buyerId only. For the
// "all" view we just render it without filters; for the "orphans" view we
// pass a special signal that the list ignores — orphans render below.
function AllRecordingsList({ tab }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === "orphans" ? { orphans: true } : {};
      const { data } = await api.get("/voice/recordings", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="text-center text-[var(--ink-2)] text-sm py-8">
        <Loader2 size={14} className="animate-spin inline mr-2" />
        Loading recordings…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="text-center text-[var(--ink-2)] text-sm py-10 border border-dashed border-[var(--line)] rounded-lg">
        <Mic size={20} className="inline mb-2 opacity-40" />
        <div className="font-semibold">No recordings {tab === "orphans" ? "without a match" : "yet"}.</div>
        <div className="text-[11px] mt-1 opacity-75">
          Use the green &ldquo;Recorded call&rdquo; button on any Lead detail page to start your first call.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((rec) => (
        <RecordingCard key={rec.id} rec={rec} />
      ))}
    </div>
  );
}

function RecordingCard({ rec }) {
  const { id, direction, from_number, to_number, duration_seconds,
    lead_id, lead_address, buyer_id, buyer_name, coach_score, summary,
    intel_status, created_at } = rec;
  const counterparty = direction === "inbound" ? from_number : to_number;
  const m = Math.floor((duration_seconds || 0) / 60);
  const s = (duration_seconds || 0) % 60;
  const when = new Date(created_at).toLocaleString();
  const matchedTo = lead_id ? { href: `/app/leads/${lead_id}/report`, label: lead_address || "Lead" } :
                    buyer_id ? { href: `/app/buyers/${buyer_id}`, label: buyer_name || "Buyer" } :
                    null;

  return (
    <div className="card flex items-center gap-3 p-3" data-testid={`all-recording-${id}`}>
      <div className={
        "w-11 h-11 rounded-lg border flex items-center justify-center font-display font-extrabold text-sm shrink-0 " +
        (coach_score == null
          ? "bg-slate-50 text-slate-400 border-slate-200"
          : coach_score >= 85 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : coach_score >= 70 ? "bg-sky-50 text-sky-700 border-sky-200"
          : coach_score >= 50 ? "bg-amber-50 text-amber-700 border-amber-200"
          : "bg-red-50 text-red-700 border-red-200")
      }>
        {coach_score ?? "—"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[var(--ink-2)] mb-0.5 truncate">
          {direction === "inbound" ? "← In" : "→ Out"} · {counterparty || "(no number)"} ·
          {" "}{m}:{String(s).padStart(2, "0")} · {when}
        </div>
        <div className="text-sm leading-snug line-clamp-2 mb-1">
          {summary?.tldr || (intel_status === "pending" || intel_status === "transcribing" || intel_status === "summarizing"
            ? "Processing AI summary…"
            : intel_status === "failed"
            ? "AI processing failed."
            : "No summary.")}
        </div>
        {matchedTo ? (
          <Link to={matchedTo.href} className="text-[11px] text-emerald-700 hover:underline font-semibold inline-flex items-center gap-1">
            Linked → {matchedTo.label}
          </Link>
        ) : (
          <span className="text-[11px] text-amber-700 font-semibold inline-flex items-center gap-1">
            <AlertTriangle size={10} /> Not matched to any lead or buyer
          </span>
        )}
      </div>
    </div>
  );
}
